import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, X } from "lucide-react";

export default function PaywallBanner() {
  const { currentOrgId, currentRole } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!currentOrgId) return;
    (async () => {
      try {
        const r = await apiFetch(`/access/status?organization_id=${currentOrgId}`);
        setStatus(r);
      } catch (_) { setStatus(null); }
    })();
  }, [currentOrgId]);

  if (!status || dismissed) return null;

  // Show banner when trialing (with days remaining) or when access is inactive/expired
  const now = new Date();
  const trialEnd = status.trial_ends_at ? new Date(status.trial_ends_at) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24))) : null;

  let variant = null;
  let title = null;
  let cta = "Manage subscription";

  if (!status.active) {
    variant = "error";
    if (status.reason === "trial_expired") {
      title = "Your trial has ended. Upgrade to continue using ClientDesk VA.";
    } else if (status.reason === "canceled") {
      title = "Your subscription is canceled. Reactivate to keep access.";
    } else if (status.reason === "unpaid") {
      title = "Your subscription payment failed. Update your billing details.";
    } else {
      title = "No active subscription. Choose a plan to unlock the workspace.";
      cta = "Choose a plan";
    }
  } else if (status.reason === "trialing" && daysLeft !== null && daysLeft <= 5) {
    variant = "warn";
    title = `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your free trial. Add a payment method to keep everything running.`;
  } else if (status.reason === "past_due_grace") {
    variant = "warn";
    title = "Your last payment failed. Please update your card to keep access.";
  }

  if (!variant) return null;

  const styles = variant === "error"
    ? "bg-red-50 border-red-200 text-red-800"
    : "bg-amber-50 border-amber-200 text-amber-900";

  const canManage = currentRole === "org_admin";

  return (
    <div className={`border rounded-2xl px-4 py-3 flex items-center gap-3 ${styles}`} data-testid="paywall-banner">
      <AlertTriangle size={16} strokeWidth={1.75} className="flex-shrink-0" />
      <div className="text-sm flex-1 min-w-0">{title}</div>
      {canManage && (
        <Link to="/app/settings/billing" data-testid="paywall-cta"
          className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#1C1917] text-white hover:bg-[#292524] whitespace-nowrap">
          {cta}
        </Link>
      )}
      <button onClick={() => setDismissed(true)} className="text-current hover:opacity-70" data-testid="paywall-dismiss">
        <X size={14} />
      </button>
    </div>
  );
}
